import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

// Agent Assist panel controller for real-time AI suggestions during calls.
// Connects to data-controller="agent-assist"
export default class extends Controller {
  static targets = ["suggestionsList", "emptyState"];
  static values = {
    callId: String
  };

  connect() {
    console.warn("AgentAssist controller connected for call:", this.callIdValue);
    this.subscribeToCallChannel();
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  subscribeToCallChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "CallChannel", id: this.callIdValue },
      {
        connected: () => {
          console.warn("AgentAssist: Connected to CallChannel");
        },

        disconnected: () => {
          console.warn("AgentAssist: Disconnected from CallChannel");
        },

        received: (data) => {
          if (data.type === "suggestion_created") {
            this.handleNewSuggestion(data);
          }
        }
      }
    );
  }

  handleNewSuggestion(data) {
    console.warn("AgentAssist: New suggestion received", data);

    // Hide empty state if present
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden");
    }

    // Build suggestion HTML
    const suggestionHtml = this.buildSuggestionCard(data);

    // Prepend new suggestion to the list
    if (this.hasSuggestionsListTarget) {
      this.suggestionsListTarget.insertAdjacentHTML("afterbegin", suggestionHtml);

      // Limit to 10 suggestions
      const suggestions = this.suggestionsListTarget.querySelectorAll("[data-suggestion-id]");
      if (suggestions.length > 10) {
        suggestions[suggestions.length - 1].remove();
      }
    }
  }

  buildSuggestionCard(data) {
    const severityClasses = this.getSeverityClasses(data.severity);
    const severityIcon = this.getSeverityIcon(data.severity);
    const sourceText = data.source_text
      ? `<p class="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">"${this.truncate(this.escapeHtml(data.source_text), 100)}"</p>`
      : "";

    return `
      <div class="rounded-lg p-3 ${severityClasses}" data-suggestion-id="${this.escapeHtml(data.id || "")}">
        <div class="flex items-start space-x-2">
          <div class="flex-shrink-0 mt-0.5">
            ${severityIcon}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
              ${this.escapeHtml(data.title || "")}
            </p>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
              ${this.escapeHtml(data.content || "")}
            </p>
            ${sourceText}
            <div class="mt-2 flex items-center space-x-2 text-xs text-gray-400 dark:text-gray-500">
              <span class="capitalize">${this.escapeHtml((data.suggestion_type || "").replace(/_/g, " "))}</span>
              <span>&middot;</span>
              <span>Just now</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  getSeverityClasses(severity) {
    switch ((severity || "").toLowerCase()) {
    case "high":
      return "bg-red-50 dark:bg-red-900/20";
    case "medium":
      return "bg-yellow-50 dark:bg-yellow-900/20";
    case "low":
      return "bg-blue-50 dark:bg-blue-900/20";
    default:
      return "bg-gray-50 dark:bg-gray-900/20";
    }
  }

  getSeverityIcon(severity) {
    switch ((severity || "").toLowerCase()) {
    case "high":
      return `<svg class="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>`;
    case "medium":
      return `<svg class="h-5 w-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>`;
    case "low":
      return `<svg class="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>`;
    default:
      return `<svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>`;
    }
  }

  escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  truncate(text, length) {
    if (!text) return "";
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  }
}
